/* Graph of Life brand v1.1.0 · tokens.json */
import React, { useEffect, useRef } from 'react';

// Live field preview. Each proof system gets its own moving signature,
// drawn from the motif recorded in the proof registry. Decorative only:
// the numbers stay in the provenance block. Animation runs only while the
// card is on screen and paints a single static frame under reduced motion.

const TEAL = [45, 212, 191];
const CORAL = [240, 112, 92];
const AMBER = [232, 181, 75];

const rgba = ([r, g, b], a) => `rgba(${r}, ${g}, ${b}, ${a})`;

// deterministic per-proof randomness, so a card always looks like itself
const seeded = (seed) => {
  let s = 0;
  for (let i = 0; i < seed.length; i += 1) s = (s * 31 + seed.charCodeAt(i)) % 2147483647;
  return () => {
    s = (s * 48271) % 2147483647;
    return s / 2147483647;
  };
};

const MOTIFS = {
  // governed spatial lattice, cells committing evidence
  lattice: (rand) => {
    const cells = [];
    const cols = 16;
    const rows = 5;
    for (let c = 0; c < cols; c += 1) {
      for (let r = 0; r < rows; r += 1) {
        cells.push({ c, r, tier: rand(), phase: rand() * Math.PI * 2 });
      }
    }
    return (ctx, w, h, t) => {
      const cw = w / cols;
      const ch = h / rows;
      cells.forEach((cell) => {
        const x = cell.c * cw + cw / 2 + (cell.r % 2 ? cw * 0.25 : 0);
        const y = cell.r * ch + ch / 2;
        const wave = Math.sin(t / 620 + cell.phase);
        const committed = cell.tier > 0.86;
        const a = committed ? 0.35 + wave * 0.35 : 0.06 + cell.tier * 0.1;
        ctx.strokeStyle = rgba(TEAL, a);
        ctx.lineWidth = committed ? 1 : 0.6;
        ctx.beginPath();
        ctx.rect(x - cw * 0.3, y - ch * 0.3, cw * 0.6, ch * 0.6);
        ctx.stroke();
        if (committed) {
          ctx.fillStyle = rgba(TEAL, 0.18 + wave * 0.2);
          ctx.fill();
        }
      });
    };
  },

  // interaction network, edges appearing and dissolving, keystone pulsing
  web: (rand) => {
    const nodes = Array.from({ length: 15 }, () => ({
      x: rand(),
      y: rand(),
      vx: (rand() - 0.5) * 0.0007,
      vy: (rand() - 0.5) * 0.0009,
      keystone: false,
    }));
    nodes[3].keystone = true;
    nodes[9].keystone = true;
    return (ctx, w, h, t) => {
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0.02 || n.x > 0.98) n.vx *= -1;
        if (n.y < 0.05 || n.y > 0.95) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];
          const d = Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);
          if (d > w * 0.19) continue;
          const flicker = 0.5 + 0.5 * Math.sin(t / 380 + i * 1.7 + j);
          ctx.strokeStyle = rgba(TEAL, (1 - d / (w * 0.19)) * 0.32 * flicker);
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.x * w, a.y * h);
          ctx.lineTo(b.x * w, b.y * h);
          ctx.stroke();
        }
      }
      nodes.forEach((n, i) => {
        const pulse = n.keystone ? 1.6 + Math.sin(t / 300 + i) * 0.7 : 1.1;
        ctx.fillStyle = rgba(TEAL, n.keystone ? 0.95 : 0.5);
        ctx.beginPath();
        ctx.arc(n.x * w, n.y * h, pulse, 0, Math.PI * 2);
        ctx.fill();
      });
    };
  },

  // teleconnected moisture corridor feeding crop nodes
  corridor: (rand) => {
    const drops = Array.from({ length: 46 }, () => ({ p: rand(), lane: rand(), speed: 0.0009 + rand() * 0.0016 }));
    const sinks = [0.62, 0.78, 0.92].map((x) => ({ x, y: 0.3 + rand() * 0.45 }));
    return (ctx, w, h, t) => {
      const path = (p, lane) => {
        const x = p * w;
        const y = h * (0.5 + Math.sin(p * 5.4 + lane * 3) * (0.26 - p * 0.14));
        return [x, y];
      };
      ctx.lineWidth = 0.6;
      for (let s = 0; s < 3; s += 1) {
        ctx.strokeStyle = rgba(TEAL, 0.1);
        ctx.beginPath();
        for (let p = 0; p <= 1; p += 0.02) {
          const [x, y] = path(p, s / 2);
          if (p === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      drops.forEach((d) => {
        d.p += d.speed;
        if (d.p > 1) d.p = 0;
        const [x, y] = path(d.p, d.lane);
        ctx.fillStyle = rgba(TEAL, 0.25 + (1 - d.p) * 0.55);
        ctx.beginPath();
        ctx.arc(x, y, 1.1, 0, Math.PI * 2);
        ctx.fill();
      });
      sinks.forEach((s, i) => {
        const glow = 0.4 + 0.4 * Math.sin(t / 460 + i);
        ctx.strokeStyle = rgba(AMBER, glow);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, 3.4, 0, Math.PI * 2);
        ctx.stroke();
      });
    };
  },

  // sediment strata with a rising methane pathway
  strata: (rand) => {
    const bubbles = Array.from({ length: 16 }, () => ({ x: rand(), y: rand(), speed: 0.0016 + rand() * 0.003 }));
    return (ctx, w, h, t) => {
      for (let i = 0; i < 6; i += 1) {
        const y = (h / 6) * i + Math.sin(t / 900 + i) * 1.5;
        ctx.strokeStyle = rgba(TEAL, 0.07 + i * 0.02);
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
      bubbles.forEach((b, i) => {
        b.y -= b.speed;
        if (b.y < 0) {
          b.y = 1;
          b.x = ((i * 37) % 100) / 100;
        }
        const drift = Math.sin(t / 400 + i) * 3;
        ctx.fillStyle = rgba(i % 4 === 0 ? CORAL : TEAL, 0.28 + (1 - b.y) * 0.5);
        ctx.beginPath();
        ctx.arc(b.x * w + drift, b.y * h, 1.2 + (1 - b.y) * 1.4, 0, Math.PI * 2);
        ctx.fill();
      });
    };
  },

  // watershed with monitoring stations scoring the model
  gauges: (rand) => {
    const stations = Array.from({ length: 13 }, (_, i) => ({
      x: 0.06 + i * 0.072,
      base: 0.3 + rand() * 0.5,
      phase: rand() * Math.PI * 2,
      loses: rand() > 0.72,
    }));
    return (ctx, w, h, t) => {
      ctx.strokeStyle = rgba(TEAL, 0.12);
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let p = 0; p <= 1; p += 0.02) {
        const x = p * w;
        const y = h * (0.82 - Math.sin(p * 3.1) * 0.12);
        if (p === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      stations.forEach((s) => {
        const val = s.base + Math.sin(t / 520 + s.phase) * 0.16;
        const top = h * (0.78 - val * 0.6);
        ctx.strokeStyle = rgba(s.loses ? CORAL : TEAL, s.loses ? 0.7 : 0.5);
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(s.x * w, h * 0.78);
        ctx.lineTo(s.x * w, top);
        ctx.stroke();
        ctx.fillStyle = rgba(s.loses ? CORAL : TEAL, 0.9);
        ctx.beginPath();
        ctx.arc(s.x * w, top, 1.4, 0, Math.PI * 2);
        ctx.fill();
      });
    };
  },

  // hyperspectral bands scanned against a separability threshold
  spectrum: (rand) => {
    const bands = Array.from({ length: 34 }, () => ({ amp: 0.2 + rand() * 0.75, phase: rand() * Math.PI * 2 }));
    return (ctx, w, h, t) => {
      const scan = ((t / 26) % (w + 90)) - 45;
      const bw = w / bands.length;
      bands.forEach((b, i) => {
        const x = i * bw + bw / 2;
        const near = Math.max(0, 1 - Math.abs(x - scan) / 70);
        const amp = b.amp * (0.7 + Math.sin(t / 700 + b.phase) * 0.18);
        const top = h * (0.9 - amp * 0.72);
        const strong = amp > 0.62;
        ctx.strokeStyle = rgba(strong ? TEAL : AMBER, 0.16 + near * 0.7);
        ctx.lineWidth = bw * 0.42;
        ctx.beginPath();
        ctx.moveTo(x, h * 0.9);
        ctx.lineTo(x, top);
        ctx.stroke();
      });
      ctx.strokeStyle = rgba(TEAL, 0.22);
      ctx.setLineDash([3, 4]);
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(0, h * 0.44);
      ctx.lineTo(w, h * 0.44);
      ctx.stroke();
      ctx.setLineDash([]);
    };
  },
};

export const ProofFieldPreview = ({ proof }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const builder = MOTIFS[proof.motif] || MOTIFS.web;
    const draw = builder(seeded(proof.id));

    let w = 0;
    let h = 0;
    let frameId;
    let visible = true;
    let last = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = (t) => {
      if (t - last > 33) {
        last = t;
        ctx.clearRect(0, 0, w, h);
        draw(ctx, w, h, t);
      }
      if (!reduced && visible) frameId = requestAnimationFrame(frame);
    };

    resize();
    frame(0);

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !reduced && !frameId) frameId = requestAnimationFrame(frame);
        if (!visible && frameId) {
          cancelAnimationFrame(frameId);
          frameId = undefined;
        }
      },
      { rootMargin: '120px' }
    );
    io.observe(canvas);
    window.addEventListener('resize', resize);

    return () => {
      io.disconnect();
      window.removeEventListener('resize', resize);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [proof.id, proof.motif]);

  return (
    <div
      data-testid={`proof-preview-${proof.id}`}
      className="relative h-24 md:h-28 -mx-7 md:-mx-9 -mt-7 md:-mt-9 mb-1 border-b border-abyss-800 overflow-hidden"
    >
      <div className="gol-grid absolute inset-0 opacity-50" aria-hidden="true" />
      <canvas ref={canvasRef} aria-hidden="true" role="presentation" className="relative w-full h-full" />
      <span className="absolute left-7 md:left-9 bottom-2 font-mono text-[9px] uppercase tracking-[0.2em] text-teal-400/60 bg-abyss-950/75 px-1.5 py-0.5 rounded-sm backdrop-blur-sm">
        {proof.previewLabel}
      </span>
    </div>
  );
};

export default ProofFieldPreview;
